/*
https://github.com/dbfannin/ngx-logger/blob/HEAD/docs/customising.md
And now everytime you have 'SERVER' in your message, the log will be sent to your server

(example) Adds a property to the server log
This example adds a property "levelName" to the body sent to the API

NB : This example can be used to change the body however you like

Tweak the server service :

@Injectable()
export class ServerCustomisedService extends NGXLoggerServerService {


  public customiseRequestBody(metadata: INGXLoggerMetadata): any {
    let body = { ...metadata };
    body['levelName'] = NgxLoggerLevel[metadata.level];

    // note, for the example we log the body but in a real case the log is useless
    console.log('Customised body is', body);

    return body;
  }
}
Provide the customised service to the logger

LoggerModule.forRoot(
  { level: NgxLoggerLevel.TRACE, serverLogLevel: NgxLoggerLevel.TRACE, serverLoggingUrl: 'dummyURL' },
  {
    serverProvider: {
      provide: TOKEN_LOGGER_SERVER_SERVICE, useClass: ServerCustomisedService
    }
  }),
And now another property levelName will be sent to your API
*/

import { Injectable, NgZone } from "@angular/core";
import { INGXLoggerConfig, INGXLoggerMetadata, NGXLoggerServerService, NgxLoggerLevel } from "ngx-logger";
import { LibraryConfigurationService } from './library-configuration.service';
import { LogUserContextService } from './log-user-context.service';
import { HttpBackend, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';

@Injectable()
export class NgxLoggerCustomServerService extends NGXLoggerServerService {
    /**
     * Reports whose message or additional data matches one of these never reach the server.
     * They are raised by browser extensions or inside SDKs, carry no reference to our own code,
     * and single clients can produce them in an endless loop. They stay visible in the console.
     */
    private static readonly DROP_PATTERNS: RegExp[] = [
        /chrome-extension:\/\//,                         // browser extensions (stack frames in message or additional)
        /moz-extension:\/\//,
        /safari-web-extension:\/\//,
        /Connection to Indexed Database server lost/,    // Firebase SDK internal, loops endlessly in some browsers
        /Database deleted by request of the user/,       // Firebase SDK internal, site data cleared while the page is open - loops the same way
        /runtime\.sendMessage\(\)\. Tab not found/,      // extension messaging
        /No Listener: tabs:outgoing\.message\.ready/,    // extension messaging
        /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/, // browser noise
        /\(auth\/network-request-failed\)/,              // Firebase Auth: browser cannot reach the auth servers (offline, ad blocker, VPN) - a state on the user's side, not a defect
    ];
    /** An identical message is sent at most once per window; repeats are counted instead. */
    private static readonly DEDUPE_WINDOW_MS = 60_000;
    private static readonly RATE_WINDOW_MS = 60_000;
    private static readonly MAX_PER_MINUTE = 10;
    private static readonly MAX_PER_PAGE = 50;
    private static readonly RATE_LIMIT_MESSAGE = 'uilogging rate limit reached, further reports suppressed';

    /** message -> start of the dedupe window it is currently in */
    private readonly dedupeWindowStart = new Map<string, number>();
    /** message -> how often it was swallowed since the last report that carried the counts */
    private readonly suppressedCounts = new Map<string, number>();
    /** timestamps of the reports actually sent, pruned to RATE_WINDOW_MS */
    private readonly sentTimestamps: number[] = [];
    private sentTotal = 0;
    private rateLimitReached = false;

    constructor(
        public config: LibraryConfigurationService,
        protected httpBackend: HttpBackend,
        protected ngZone: NgZone,
        private logUserContext: LogUserContextService) {
        super(httpBackend, ngZone); // Pass the required arguments to the super() call
    }

    /**
     * Everything the app logs at serverLogLevel passes through here. The counters live on the
     * service instance only - a reload starts over, which is intended.
     */
    public override sendToServer(metadata: INGXLoggerMetadata, config: INGXLoggerConfig): void {
        // dropped reports are not counted and not reported as suppressed - they just disappear
        if (this.isDropped(metadata)) return;
        if (this.rateLimitReached) return;

        const now = Date.now();
        const key = this.dedupeKey(metadata);
        const windowStart = this.dedupeWindowStart.get(key);
        if (windowStart !== undefined && now - windowStart < NgxLoggerCustomServerService.DEDUPE_WINDOW_MS) {
            this.suppressedCounts.set(key, (this.suppressedCounts.get(key) ?? 0) + 1);
            return;
        }
        this.dedupeWindowStart.set(key, now);

        this.pruneSentTimestamps(now);
        if (this.sentTimestamps.length >= NgxLoggerCustomServerService.MAX_PER_MINUTE
            || this.sentTotal >= NgxLoggerCustomServerService.MAX_PER_PAGE) {
            this.rateLimitReached = true;
            // one last report so the gap is visible in the log, then the channel stays closed
            super.sendToServer({
                level: NgxLoggerLevel.ERROR,
                message: NgxLoggerCustomServerService.RATE_LIMIT_MESSAGE,
                additional: [{
                    sentInLastMinute: this.sentTimestamps.length,
                    sentThisPage: this.sentTotal,
                    maxPerMinute: NgxLoggerCustomServerService.MAX_PER_MINUTE,
                    maxPerPage: NgxLoggerCustomServerService.MAX_PER_PAGE,
                }],
                timestamp: new Date().toISOString(),
            }, config);
            return;
        }

        this.sentTimestamps.push(now);
        this.sentTotal++;
        super.sendToServer(metadata, config);
    }

    public customiseRequestBody(metadata: INGXLoggerMetadata): any {
        let body = { ...metadata };
        body['levelName'] = NgxLoggerLevel[metadata.level];
        body['version'] = this.config.version ? this.config.version : 'no-version-provided'
        body['host'] = this.config.host ? this.config.host : 'no-host-provided'
        // only when the app knows a user - the server counts affected users from it, an absent
        // field simply means "not signed in yet" and must not become an empty-string user
        const userId = this.logUserContext?.userId;
        if (userId) {
            body['userId'] = userId;
        }
        if (this.suppressedCounts.size > 0) {
            // ride along on whatever goes out next, so repeats stay visible without a post each
            const suppressed: { [message: string]: number } = {};
            this.suppressedCounts.forEach((count, message) => suppressed[message] = count);
            body['suppressed'] = suppressed;
            this.suppressedCounts.clear();
        }
        return body;
    }

    private isDropped(metadata: INGXLoggerMetadata): boolean {
        const text = this.buildFilterText(metadata);
        return NgxLoggerCustomServerService.DROP_PATTERNS.some(pattern => pattern.test(text));
    }

    /**
     * Message plus additional data as one string - extension frames often sit only in the stack
     * that was passed as an additional parameter.
     */
    private buildFilterText(metadata: INGXLoggerMetadata): string {
        const parts: string[] = [this.messageText(metadata)];
        (metadata.additional ?? []).forEach(entry => {
            if (entry instanceof Error) {
                parts.push(entry.stack ?? entry.message);
            } else if (typeof entry === 'string') {
                parts.push(entry);
            } else {
                try {
                    parts.push(JSON.stringify(entry));
                } catch {
                    // circular structure - nothing to match on, the message alone has to do
                }
            }
        });
        return parts.join(' ');
    }

    /** Dedupe on the message alone - including additional would let stack variants slip through. */
    private dedupeKey(metadata: INGXLoggerMetadata): string {
        return this.messageText(metadata);
    }

    private messageText(metadata: INGXLoggerMetadata): string {
        const message = metadata.message;
        if (typeof message === 'string') return message;
        if (message === undefined || message === null) return '';
        return String(message);
    }

    private pruneSentTimestamps(now: number): void {
        while (this.sentTimestamps.length > 0
            && now - this.sentTimestamps[0] >= NgxLoggerCustomServerService.RATE_WINDOW_MS) {
            this.sentTimestamps.shift();
        }
    }
}
